import React from 'react';
import { motion } from 'framer-motion';
import { useInventory } from '../../contexts/InventoryContext';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBox, FiDollarSign, FiTrendingUp, FiArrowLeft } = FiIcons;

const PalletReport = () => {
  const { pallets, products, inventoryHistory } = useInventory();
  const navigate = useNavigate();

  // Calculate Metrics for each pallet
  const palletMetrics = pallets.map(pallet => {
    // 1. Find all products belonging to this pallet
    const palletProducts = products.filter(p => p.pallet_id === pallet.id);
    
    // 2. Calculate Revenue (Items sold * Local Price)
    let revenue = 0;
    let itemsSold = 0;
    let itemsInStock = 0;
    let inventoryValue = 0;

    palletProducts.forEach(product => {
      // Current stock value
      itemsInStock += product.quantity;
      const price = product.localPrice || 0;
      inventoryValue += (product.quantity * price);

      // Sold items calculation (using history)
      const soldHistory = inventoryHistory.filter(h => 
        h.productId === product.id && 
        (h.type === 'sale' || h.type === 'sale_reconciliation')
      );
      
      const soldCount = soldHistory.reduce((sum, h) => sum + Math.abs(h.quantity), 0);
      itemsSold += soldCount;
      revenue += (soldCount * price);
    });

    const palletCost = parseFloat(pallet.cost) || 0;
    const totalReturn = revenue + inventoryValue; // Realized + Potential
    const netProfit = revenue - palletCost; // Cash profit (Sales - Cost)
    const projectedProfit = totalReturn - palletCost;

    return {
      ...pallet,
      itemsSold,
      itemsInStock,
      revenue,
      inventoryValue,
      netProfit,
      projectedProfit,
      cost: palletCost
    };
  });

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Pallet Profitability Report</h1>
          <p className="text-gray-600 dark:text-gray-400">Analyze ROI and performance for each inventory pallet.</p>
        </div>
        <button
          onClick={() => navigate('/pallets')}
          className="flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2 h-5 w-5" />
          Back to Pallets
        </button>
      </motion.div>

      <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Pallet</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cost</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Revenue (Sales)</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Inventory Value</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Net Cash Profit</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Projected Total</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Items (Sold/Stock)</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {palletMetrics.map((pallet) => (
              <tr key={pallet.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900 dark:text-white">{pallet.pallet_number}</div>
                  <div className="text-xs text-gray-500">{new Date(pallet.purchase_date).toLocaleDateString()}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 dark:text-white">
                  ${pallet.cost.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-green-600 font-medium">
                  ${pallet.revenue.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-blue-600">
                  ${pallet.inventoryValue.toFixed(2)}
                </td>
                <td className={`px-6 py-4 whitespace-nowrap text-right font-bold ${pallet.netProfit >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  ${pallet.netProfit.toFixed(2)}
                </td>
                <td className={`px-6 py-4 whitespace-nowrap text-right font-medium ${pallet.projectedProfit >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  ${pallet.projectedProfit.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600 dark:text-gray-400">
                  {pallet.itemsSold} / {pallet.itemsInStock}
                </td>
              </tr>
            ))}
            {palletMetrics.length === 0 && (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                  No pallets found. Add pallets to start tracking profitability.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PalletReport;